import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ModuloBloqueable, PatchProyectoPayload } from '@erp/shared';
import * as api from '@api/proyectos';
import type {
  FiltrosProyectos, CrearProyectoPayload, CrearUnidadPayload, EditarUnidadPayload, CargarObjetivosPayload, CrearPartidaPayload,
  CrearSuplementoPayload, GuardarConfigCotizadorPayload,
  CrearPlantillaPayload, ActualizarPlantillaPayload, TipoPlantilla,
  CrearDocumentoPayload, BypassDocumentoPayload, CrearAlertaPayload,
  ActualizarParametrosPayload, ResumenFinanciero,
} from '@/types/proyectos';

const KEY = 'proyectos';

/**
 * La lista de proyectos. `opciones.enabled` sigue el mismo patrón que `useProyecto`
 * y `useRentabilidad`, unos renglones más abajo — no es una forma nueva.
 *
 * 🔴 CORRECCIÓN A LO QUE DECÍA AQUÍ (BUG-481). Este comentario afirmaba que el `enabled`
 *    evitaba *«traer datos de OTROS proyectos»* y *«una consulta de red en cada montaje»*.
 *    **Las dos son falsas dentro de «El Proyecto», y lo medí después de escribirlas:**
 *    `pages/CasaDelProyecto.tsx:53` —el contenedor que monta la pestaña— ya pedía
 *    `useProyectos` con la página fija de 100 y el filtro IDÉNTICO ⇒ **la misma
 *    `queryKey`** (I-228: hoy ese contenedor pide `useTodosLosProyectos()`, otra clave) ⇒ la petición se dispara igual, y una `useQuery` con `enabled:false` sigue
 *    leyendo la caché de su clave. Lo que impide pintar el selector es el `{!proyectoId && …}`
 *    de `RecurrentesTab.tsx:311`, que ya estaba antes de este cambio.
 *
 * 🟢 Para qué SÍ sirve, que es lo único cierto: es el contrato correcto del hook. Un
 *    componente que no necesita la lista no tiene por qué pedirla, y el día que se monte
 *    fuera del contenedor —o que el padre deje de pedirla— el ahorro se vuelve real.
 *    **Se deja porque es correcto, no porque hoy ahorre algo.**
 *
 * 🔑 Por qué se corrige un comentario que no rompe nada: **el código miente igual cuando
 *    presume de más que cuando dice de menos.** Un beneficio afirmado y no medido es la misma
 *    clase de defecto que una advertencia falsa — y el siguiente que lo lea lo dará por cierto.
 *
 *    Los 12 call-sites de ESTE hook que pasan un solo argumento siguen igual: el parámetro es
 *    opcional. (Antes decía 13, contando `M13/SeccionUsuarios.tsx:63` — que **no pasa ninguno
 *    y ni siquiera es este hook**: viene de `./ConfigShared`.)
 */
export const useProyectos = (f: FiltrosProyectos, opciones?: { enabled?: boolean }) =>
  useQuery({ queryKey: [KEY, f], queryFn: () => api.fetchProyectos(f), staleTime: 30_000,
             enabled: opciones?.enabled ?? true });

/** I-228: la lista COMPLETA de proyectos para selectores (la misma forma que `useProyectos`, más `truncada`). */
export const useTodosLosProyectos = (opciones?: { enabled?: boolean }) =>
  useQuery({ queryKey: [KEY, 'todos'], queryFn: () => api.fetchTodosLosProyectos(), staleTime: 30_000,
             enabled: opciones?.enabled ?? true });

export interface ProyectoSimple { id: number; nombre: string }

/** Lista simple {id, nombre} para selects/checklists — no la paginada de arriba. */
export const useProyectosSimple = (opciones?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [KEY, 'simple'],
    queryFn: () => api.fetchTodosLosProyectos()
      .then(r => r.data.map((p): ProyectoSimple => ({ id: p.id, nombre: p.nombre }))),
    staleTime: 60_000,
    enabled: opciones?.enabled ?? true,
  });

export const useProyecto = (id: number | null) =>
  useQuery({ queryKey: [KEY, id], queryFn: () => api.fetchProyecto(id!), enabled: id != null && Number.isFinite(id), staleTime: 30_000 });

export const useRentabilidad = (proyectoId: number | null) =>
  useQuery({ queryKey: [KEY, 'rentabilidad', proyectoId], queryFn: () => api.fetchRentabilidad(proyectoId!), enabled: proyectoId !== null, staleTime: 60_000 });

export const useRecalcularRentabilidad = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.recalcularRentabilidad(proyectoId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'rentabilidad', proyectoId] }),
  });
};

export const useAlertas = (proyectoId: number | null, silenciarCandado = false, enabled = true) =>
  useQuery({ queryKey: [KEY, 'alertas', proyectoId], queryFn: () => api.fetchAlertas(proyectoId!, silenciarCandado), enabled: proyectoId !== null && enabled, staleTime: 30_000 });

export const useRitmoVenta = (proyectoId: number | null, silenciarCandado = false, enabled = true) =>
  useQuery({
    queryKey: [KEY, 'ritmo-venta', proyectoId],
    queryFn: () => api.fetchRitmoVenta(proyectoId!, silenciarCandado),
    enabled: proyectoId !== null && enabled,
    staleTime: 60_000,
  });

export const useAtencion = (proyectoId: number | null) =>
  useQuery({ queryKey: [KEY, 'atencion', proyectoId], queryFn: () => api.fetchAtencion(proyectoId!), enabled: proyectoId !== null, staleTime: 30_000 });

export const useCrearProyecto = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CrearProyectoPayload) => api.crearProyecto(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

// ─── Unidades ────────────────────────────────────────────────────
export const useUnidades = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'unidades', proyectoId],
    queryFn: () => api.fetchUnidades(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 30_000,
  });

/**
 * I-222 (T13·1) — el valor del inventario del proyecto completo: lo calcula el servidor y la pantalla
 * de Unidades sólo lo pinta. La llave cuelga de la de `useUnidades`, así que cualquier mutación que
 * invalide las unidades (`[KEY,'unidades',id]` o `['proyectos','unidades']`) lo refresca también.
 */
export const useTotalesDeUnidades = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'unidades', proyectoId, 'totales'],
    queryFn: () => api.fetchTotalesDeUnidades(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 30_000,
  });

/** T4 — meta, brecha y % sugerido del proyecto. */
export const useObjetivoVenta = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'unidades', proyectoId, 'objetivo-venta'],
    queryFn: () => api.fetchObjetivoVenta(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 30_000,
  });

export const useContratosProyecto = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'contratos', proyectoId],
    queryFn: () => api.fetchContratosProyecto(proyectoId!),
    enabled: proyectoId !== null && proyectoId > 0,
    staleTime: 60_000,
  });

export const useCrearUnidad = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CrearUnidadPayload) => api.crearUnidad({ proyectoId, payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'unidades', proyectoId] }),
  });
};

export const useEditarUnidad = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unidadId, payload }: { unidadId: number; payload: EditarUnidadPayload }) =>
      api.editarUnidad({ proyectoId, unidadId, payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'unidades', proyectoId] }),
  });
};

// BUG-4178 — carga los objetivos que faltan en UNA transacción del servidor (la vista previa `solo_vista` no escribe, no invalida nada).
export const useCargarObjetivos = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CargarObjetivosPayload) => api.cargarObjetivos({ proyectoId, payload }),
    onSuccess: (_r, payload) => {
      if (payload.modo === 'pct' && payload.solo_vista) return;
      return qc.invalidateQueries({ queryKey: [KEY, 'unidades', proyectoId] });
    },
  });
};

// I-52 (BUG-1153) — sólo N4, con nombre exacto + motivo ≥30 chars (el servidor los exige).
export const useEliminarUnidad = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unidadId, nombre, motivo }: { unidadId: number; nombre: string; motivo: string }) =>
      api.eliminarUnidad({ proyectoId, unidadId, nombre, motivo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'unidades', proyectoId] }),
  });
};

export const useImportarUnidadesExcel = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (archivo: File) => api.importarUnidadesExcel({ proyectoId, archivo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'unidades', proyectoId] }),
  });
};

export const usePreviewImportarUnidades = (proyectoId: number) =>
  useMutation({
    mutationFn: (archivo: File) => api.previewImportarUnidadesExcel(proyectoId, archivo),
  });

// ─── D-2026-09-09-F — Catálogo de etapas del proyecto ────────────

/**
 * "Asegurar" una lista de claves — usada por `RejillaCargador` (`onAsegurarEtapas`)
 * ANTES de importar un lote: cada torre que la persona tecleó en la pantalla tiene
 * que existir en el catálogo o el importador la rechaza entera. Un 409 (ya existe)
 * cuenta como éxito — no es un error, es la razón por la que se puede repetir.
 */
export const useAsegurarEtapasProyecto = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (claves: string[]) => {
      for (const clave of claves) {
        try {
          await api.crearEtapaProyecto({ proyectoId, clave });
        } catch (err) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status !== 409) throw err;
        }
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'etapas', proyectoId] }),
  });
};

// ─── Catálogo Presupuesto ─────────────────────────────────────────
export const usePresupuesto = (proyectoId: number | null, silenciarCandado = false, enabled = true) =>
  useQuery({
    queryKey: [KEY, 'presupuesto', proyectoId],
    queryFn: () => api.fetchPresupuesto(proyectoId!, silenciarCandado),
    enabled: proyectoId !== null && enabled,
    staleTime: 30_000,
  });

export const useCrearPartida = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CrearPartidaPayload) => api.crearPartida({ proyectoId, payload }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [KEY, 'presupuesto', proyectoId] });
      // I-240 (D-2026-09-30-H): si nació «Sin desglosar», el servidor lo DICE y la pantalla lo repite — nada mudo.
      if (r?.sin_desglosar && r.message) toast.success(r.message, { duration: 9000 });
    },
  });
};

export const useEditarPartida = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ partidaId, payload }: { partidaId: number; payload: { nombre?: string; monto_presupuestado?: string; fecha_inicio?: string | null; fecha_fin?: string | null; metodo_pago?: 'transferencia' | 'cheque' | 'efectivo' | null } }) =>
      api.editarPartida({ proyectoId, partidaId, payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'presupuesto', proyectoId] }),
  });
};

export const useEliminarPartida = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partidaId: number) => api.eliminarPartida({ proyectoId, partidaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'presupuesto', proyectoId] }),
  });
};

export const useCerrarPresupuesto = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.cerrarPresupuesto(proyectoId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'presupuesto', proyectoId] });
      qc.invalidateQueries({ queryKey: [KEY, proyectoId] });
    },
  });
};

// I-174 — endpoint propio (`authN4rls`), separado de `useCerrarPresupuesto`. Antes "Reabrir"
// llamaba al mismo hook con `cerrar=false` y el servidor lo ignoraba (ver api/proyectos.ts).
export const useAbrirPresupuesto = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.abrirPresupuesto(proyectoId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'presupuesto', proyectoId] });
      qc.invalidateQueries({ queryKey: [KEY, proyectoId] });
    },
  });
};

export const useToggleBloquearPartida = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ partidaId, bloqueada }: { partidaId: number; bloqueada: boolean }) =>
      api.toggleBloquearPartida({ proyectoId, partidaId, bloqueada }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'presupuesto', proyectoId] }),
  });
};

// ─── Suplementos ─────────────────────────────────────────────────
export const useSuplementos = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'suplementos', proyectoId],
    queryFn: () => api.fetchSuplementos(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 30_000,
  });

export const useCrearSuplemento = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CrearSuplementoPayload) => api.crearSuplemento({ proyectoId, payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'presupuesto', proyectoId] });
      qc.invalidateQueries({ queryKey: [KEY, 'suplementos', proyectoId] });
    },
  });
};

export const useAutorizarSuplemento = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ suplementoId, nota }: { suplementoId: number; nota?: string }) =>
      api.autorizarSuplemento({ proyectoId, suplementoId, nota }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'presupuesto', proyectoId] });
      qc.invalidateQueries({ queryKey: [KEY, 'suplementos', proyectoId] });
    },
  });
};

// ─── Gantt ───────────────────────────────────────────────────────
export const useGantt = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'gantt', proyectoId],
    queryFn: () => api.fetchGantt(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 60_000,
  });

// ─── Documentos ──────────────────────────────────────────────────
export const useDocumentos = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'documentos', proyectoId],
    queryFn: () => api.fetchDocumentos(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 60_000,
  });

export const useCrearDocumento = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, archivo }: { payload: CrearDocumentoPayload; archivo: File }) =>
      api.crearDocumento({ proyectoId, payload, archivo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'documentos', proyectoId] }),
  });
};

export const useEliminarDocumento = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentoId: number) => api.eliminarDocumento({ proyectoId, documentoId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'documentos', proyectoId] }),
  });
};

export const useBypassDocumento = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BypassDocumentoPayload) => api.bypassDocumento({ proyectoId, payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'documentos', proyectoId] }),
  });
};

// ─── Mapa acciones ──────────────────────────────────────────────
export const useBloquearMapa = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.bloquearMapa(proyectoId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'mapa', proyectoId] }),
  });
};

export const useDesbloquearMapa = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.desbloquearMapa(proyectoId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'mapa', proyectoId] }),
  });
};

export const useResolverAlerta = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertaId, nota_resolucion }: { alertaId: number; nota_resolucion: string }) =>
      api.resolverAlerta({ proyectoId, alertaId, nota_resolucion }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'alertas', proyectoId] }),
  });
};

export const useCrearAlerta = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CrearAlertaPayload) => api.crearAlerta({ proyectoId, payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'alertas', proyectoId] }),
  });
};

/** Quiénes trabajan en ESTE proyecto — bloque «Gente» de Config. */
export const useUsuariosDelProyecto = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'usuarios-del-proyecto', proyectoId],
    queryFn: () => api.fetchUsuariosDelProyecto(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 60_000,
  });

// ─── Config cotizador ────────────────────────────────────────────
export const useConfigCotizador = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'cotizador-config', proyectoId],
    queryFn: () => api.fetchConfigCotizador(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 60_000,
  });

export const useGuardarConfigCotizador = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GuardarConfigCotizadorPayload) => api.guardarConfigCotizador({ proyectoId, payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, 'cotizador-config', proyectoId] });
      // BUG-2177 — el vencimiento de cada apartado viaja en la lista de unidades
      // (`apartado_vence_en`), calculado con el plazo que se acaba de guardar.
      qc.invalidateQueries({ queryKey: [KEY, 'unidades', proyectoId] });
    },
  });
};

// ─── Plantillas ──────────────────────────────────────────────────
export const usePlantillas = (proyectoId: number | null, tipo?: TipoPlantilla) =>
  useQuery({
    queryKey: [KEY, 'plantillas', proyectoId, tipo],
    queryFn: () => api.fetchPlantillas(proyectoId!, tipo),
    enabled: proyectoId !== null,
    staleTime: 30_000,
  });

export const useCrearPlantilla = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, archivo }: { payload: CrearPlantillaPayload; archivo?: File }) =>
      api.crearPlantilla({ proyectoId, payload, archivo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'plantillas', proyectoId] }),
  });
};

export const useActualizarPlantilla = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ plantillaId, payload }: { plantillaId: number; payload: ActualizarPlantillaPayload }) =>
      api.actualizarPlantillaApi({ proyectoId, plantillaId, payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'plantillas', proyectoId] }),
  });
};

// ─── Cierre de proyecto ──────────────────────────────────────────
// I-174 B — `nota` opcional: sólo viaja cuando el CEO está forzando el cierre con saldos
// pendientes (SALDOS_PENDIENTES_AL_CERRAR). El primer intento nunca la manda.
export const useCerrarProyecto = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars?: { modulos?: ModuloBloqueable[]; nota?: string }) =>
      api.cerrarProyecto(proyectoId, vars?.modulos, vars?.nota),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, proyectoId] }),
  });
};

export const useAbrirProyecto = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.abrirProyecto(proyectoId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, proyectoId] }),
  });
};

// ─── Patch proyecto ───────────────────────────────────────────
export const usePatchProyecto = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    // BUG-280 — además de la entidad fiscal, aquí se guarda la comisión sugerida del proyecto.
    // tipo_proyecto (cotejo de diseño, 2026-09-08) — mismo PATCH, D-2026-09-04 ya lo habilitó.
    // I-88 T4 (BUG-1500) — payload con nombre (PatchProyectoPayload, @erp/shared): antes tipo
    // anónimo duplicado a mano aquí Y en api/proyectos.ts:83.
    mutationFn: (data: PatchProyectoPayload) => api.patchProyecto(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, id] });
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
};

// ─── Parámetros de proyecto ───────────────────────────────────
export const useParametros = (proyectoId: number | null) =>
  useQuery({
    queryKey: [KEY, 'parametros', proyectoId],
    queryFn: () => api.fetchParametros(proyectoId!),
    enabled: proyectoId !== null,
    staleTime: 60_000,
  });

export const useActualizarParametros = (proyectoId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ActualizarParametrosPayload) => api.actualizarParametros({ proyectoId, payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'parametros', proyectoId] }),
  });
};

// ─── Resumen Financiero ────────────────────────────────────────────
export const useResumenFinanciero = (id: number, silenciarCandado = false, enabled = true) =>
  useQuery<ResumenFinanciero>({
    queryKey: ['proyecto-resumen', id],
    queryFn:  () => api.fetchResumenFinanciero(id, silenciarCandado),
    staleTime: 30_000,
    enabled:  id > 0 && enabled,
  });

/**
 * Las cuentas del proyecto con su saldo — bloque 01 de la casa.
 * `staleTime` corto: es dinero, y un saldo viejo es peor que uno que tarda medio segundo.
 */
/**
 * El visto bueno de la semana. `staleTime` corto porque el acuse puede llegar de otra
 * persona mientras tienes la pantalla abierta.
 */
export const useVistoBueno = (proyectoId: number | null, activo = true) =>
  useQuery({
    queryKey: ['proyectos', proyectoId, 'visto-bueno'],
    queryFn: () => api.fetchVistoBueno(proyectoId!),
    enabled: proyectoId !== null && activo,
    staleTime: 15_000,
  });

/** Palomear la semana. No mueve dinero: sólo se refresca su propio acuse. */
export const usePalomearSemana = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proyectoId: number) => api.palomearSemana(proyectoId),
    onSuccess: (_d, proyectoId) =>
      qc.invalidateQueries({ queryKey: ['proyectos', proyectoId, 'visto-bueno'] }),
  });
};

/** BUG-3872 — `habilitado`: el servidor pide el módulo de egresos; quien no lo tiene no pregunta (si no, recibe un 403 y un aviso rojo). */
export const useCuentasDelProyecto = (proyectoId: number | null, habilitado = true) =>
  useQuery({
    queryKey: ['proyectos', proyectoId, 'cuentas'],
    queryFn: () => api.fetchCuentasDelProyecto(proyectoId!),
    enabled: proyectoId !== null && habilitado,
    staleTime: 15_000,
  });

/**
 * BUG-409 — el cruce: cuánto se le ha entregado ya a cada socio del proyecto, sumando
 * los dos caminos (M02 Egresos + M05 Créditos). Compartido a propósito entre
 * `PagosDelProyectoTab.tsx` (la casa) y `M05/DistribucionGanancias.tsx` — es la misma
 * pregunta desde los dos lados, no dos cuentas distintas.
 */
export const useCruceDistribucionesSocios = (proyectoId: number | null, activo = true) =>
  useQuery({
    queryKey: ['proyectos', proyectoId, 'distribuciones-socios-cruce'],
    queryFn: () => api.fetchCruceDistribucionesSocios(proyectoId!),
    enabled: proyectoId !== null && activo,
    staleTime: 15_000,
  });
